import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type DiscordChannelT, type DiscordInfo, IMPORT_STATE } from '../types';

export const useDiscord = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;

  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.DISCORD,
    workspace_alias,
  );

  const { data: discordWorkspaces } = useQuery({
    queryKey: queryKeys,
    queryFn: async () => {
      const data = await service.discord.getDiscordInfos(workspace_alias);
      if (data) {
        return data;
      }
      return [];
    },
    enabled: enabled,
    placeholderData: [],
  });

  const { mutate: disconnectDiscordMutation } = useMutation({
    mutationFn: (alias: string) => service.discord.deleteDiscordInfo(alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: ({
      alias,
      dateRange,
    }: {
      alias: string;
      dateRange: NullableDateRangeT;
    }) =>
      service.discord.importWorkspaceFeedback(
        alias,
        dateRange.start_date || '',
        dateRange.end_date || '',
      ),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(queryKeys, (old: any) => {
        if (!old) return old;
        return old.map((w: DiscordInfo) =>
          w.alias === variables.alias
            ? { ...w, importState: IMPORT_STATE.IMPORTING }
            : w,
        );
      });
    },
  });

  return {
    discordWorkspaces: discordWorkspaces || [],
    disconnectDiscordMutation,
    requestImportMutation,
  };
};

export const useDiscordChannels = (discordWorkspaceId: string) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = ['discord-channels', workspace_alias, discordWorkspaceId];

  const { data: channels } = useQuery({
    queryKey: queryKeys,
    queryFn: () => service.discord.getDiscordChannels(discordWorkspaceId),
    enabled: !!discordWorkspaceId,
    placeholderData: {},
  });

  // Channel Mutations

  return {
    channels,
  };
};

export const useDiscordAction = () => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.DISCORD,
    workspace_alias,
  );

  const { mutate: pushChannelMutation } = useMutation({
    mutationFn: ({
      alias,
      channels,
    }: {
      alias: string;
      channels: DiscordChannelT[];
    }) => service.discord.pushDiscordChannel(alias, channels),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: pullChannelMutation } = useMutation({
    mutationFn: ({
      alias,
      channelIds,
    }: {
      alias: string;
      channelIds: string[];
    }) => service.discord.pullDiscordChannel(alias, channelIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: selectAllChannelsMutation } = useMutation({
    mutationFn: ({
      alias,
      channels,
    }: {
      alias: string;
      channels: DiscordChannelT[];
    }) => service.discord.selectAllDiscordChannels(alias, channels),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: deselectAllChannelsMutation } = useMutation({
    mutationFn: (alias: string) =>
      service.discord.deselectAllDiscordChannels(alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: updatePeriodMutation } = useMutation({
    mutationFn: ({ alias, period }: { alias: string; period: number }) =>
      service.discord.updateDiscordPeriod(alias, period),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  return {
    pushChannelMutation,
    pullChannelMutation,
    selectAllChannelsMutation,
    deselectAllChannelsMutation,
    updatePeriodMutation,
  };
};
